"use strict";
/**
 * Extraction-run polling service. Used by UseCaseDetails to keep the
 * "Syncing analysis" loader visible until the queued ExtractionRun
 * actually finishes (success or failure), so the user does not see
 * the page flip back to stale highlights while the agent is still
 * running.
 *
 * Backed by GET /api/ai/extraction/runs/:id.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.pollExtractionRun = exports.fetchActiveExtractionRun = exports.fetchExtractionRun = exports.isTerminalExtractionStatus = void 0;
var config_1 = require("../../config");
var TERMINAL_STATUSES = new Set([
    'success',
    'schema-failed',
    'agent-error',
    'timeout',
]);
exports.isTerminalExtractionStatus = function (status) {
    return TERMINAL_STATUSES.has(status);
};
exports.fetchExtractionRun = function (runId) { return __awaiter(void 0, void 0, void 0, function () {
    var apiGet, url, response, error_1;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                _a.trys.push([0, 2, , 3]);
                apiGet = config_1.getApiGet();
                url = "/ai/extraction/runs/" + encodeURIComponent(String(runId));
                return [4 /*yield*/, apiGet(url)];
            case 1:
                response = (_a.sent());
                return [2 /*return*/, response && response.status ? response : null];
            case 2:
                error_1 = _a.sent();
                // eslint-disable-next-line no-console
                console.warn('[extraction-run] fetch failed:', error_1.message);
                return [2 /*return*/, null];
            case 3: return [2 /*return*/];
        }
    });
}); };
/**
 * Returns the currently-active (queued or running) extraction run
 * for a use case, or `null` if none exists. Used on UseCaseDetails
 * mount to restore the "syncing" banner when the user reloads or
 * navigates back mid-extraction.
 *
 * The backend returns 204 No Content when there is no active run;
 * the API helper resolves with a falsy body in that case so we map
 * it to `null` here.
 */
exports.fetchActiveExtractionRun = function (useCaseId) { return __awaiter(void 0, void 0, void 0, function () {
    var apiGet, url, response, error_2;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                _a.trys.push([0, 2, , 3]);
                apiGet = config_1.getApiGet();
                url = "/ai/extraction/use-cases/" + encodeURIComponent(useCaseId) + "/active-run";
                return [4 /*yield*/, apiGet(url)];
            case 1:
                response = (_a.sent());
                if (!response || !response.status) {
                    return [2 /*return*/, null];
                }
                return [2 /*return*/, response];
            case 2:
                error_2 = _a.sent();
                // eslint-disable-next-line no-console
                console.warn('[extraction-run] active-run fetch failed:', error_2.message);
                return [2 /*return*/, null];
            case 3: return [2 /*return*/];
        }
    });
}); };
/**
 * Polls the extraction run until it reaches a terminal status, the
 * caller cancels, or the max duration elapses. Resolves with the
 * last observed snapshot (or `null` if the run could not be read).
 */
exports.pollExtractionRun = function (runId, options) {
    if (options === void 0) { options = {}; }
    return __awaiter(void 0, void 0, void 0, function () {
        var intervalMs, maxDurationMs, startedAt, last, snapshot;
        var _a, _b, _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    intervalMs = Math.max(500, (_a = options.intervalMs) !== null && _a !== void 0 ? _a : 2000);
                    maxDurationMs = (_b = options.maxDurationMs) !== null && _b !== void 0 ? _b : 5 * 60 * 1000;
                    startedAt = Date.now();
                    last = null;
                    _e.label = 1;
                case 1:
                    if (!true) return [3 /*break*/, 4];
                    if ((_c = options.isCancelled) === null || _c === void 0 ? void 0 : _c.call(options)) {
                        return [2 /*return*/, last];
                    }
                    return [4 /*yield*/, exports.fetchExtractionRun(runId)];
                case 2:
                    snapshot = _e.sent();
                    if (snapshot) {
                        last = snapshot;
                        (_d = options.onUpdate) === null || _d === void 0 ? void 0 : _d.call(options, snapshot);
                        if (exports.isTerminalExtractionStatus(snapshot.status)) {
                            return [2 /*return*/, snapshot];
                        }
                    }
                    if (Date.now() - startedAt >= maxDurationMs) {
                        return [2 /*return*/, last];
                    }
                    return [4 /*yield*/, new Promise(function (resolve) { return setTimeout(resolve, intervalMs); })];
                case 3:
                    _e.sent();
                    return [3 /*break*/, 1];
                case 4: return [2 /*return*/];
            }
        });
    });
};
